import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import createManualHealthEvent, {
  CreateManualHealthEventRequest,
} from "../http/createManualHealthEvent";
import deleteHealthEvent from "../http/deleteHealthEvent";
import getSiteHealthEvents, {
  SiteHealthEventsResponse,
} from "../http/getSiteHealthEvents";
import updateHealthEvent, {
  UpdateHealthEventRequest,
} from "../http/updateHealthEvent";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

export const useSiteHealthEvents = (
  siteId: string | undefined,
  from?: string,
  to?: string
) => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();

  const query = useQuery<SiteHealthEventsResponse>(
    ["siteHealthEvents", siteId, from, to],
    async () => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return getSiteHealthEvents(token, siteId!, from, to);
    },
    {
      enabled: !!siteId,
      staleTime: 60_000,
    }
  );

  const updateEvent = useMutation(
    async ({ eventId, ...update }: { eventId: number } & UpdateHealthEventRequest) => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return updateHealthEvent(token, siteId!, eventId, update);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["siteHealthEvents", siteId]);
      },
    }
  );

  const createManualEvent = useMutation(
    async (event: CreateManualHealthEventRequest) => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return createManualHealthEvent(token, siteId!, event);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["siteHealthEvents", siteId]);
      },
    }
  );

  const deleteEvent = useMutation(
    async (eventId: number) => {
      const token = await getAuthTokenBlocking(getToken, relogin);
      return deleteHealthEvent(token, siteId!, eventId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["siteHealthEvents", siteId]);
      },
    }
  );

  return {
    healthEvents: query,
    updateEvent,
    createManualEvent,
    deleteEvent,
  };
};
